import React from "react";
import moment from "moment";
import { BsFileEarmarkPdf, BsPaperclip, BsBoxArrowUpRight } from "react-icons/bs";

const parseFileNameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition || typeof contentDisposition !== "string") return "";

  try {
    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));
    }

    const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
    if (quotedMatch && quotedMatch[1]) {
      return quotedMatch[1].trim();
    }

    const plainMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (plainMatch && plainMatch[1]) {
      return plainMatch[1].trim().replace(/^"|"$/g, "");
    }
  } catch (err) {
    return "";
  }

  return "";
};

const getFileNameFromUrl = (fileUrl) => {
  if (!fileUrl || typeof fileUrl !== "string") return "";

  try {
    const baseUrl = typeof window !== "undefined" && window.location ? window.location.origin : "http://localhost";
    const parsedUrl = new URL(fileUrl, baseUrl);

    // S3 signed URLs may carry the real filename in response-content-disposition.
    // Prefer that value because the path/key can be generic or encoded.
    const disposition =
      parsedUrl.searchParams.get("response-content-disposition") ||
      parsedUrl.searchParams.get("ResponseContentDisposition") ||
      "";
    const dispositionFileName = parseFileNameFromContentDisposition(disposition);
    if (dispositionFileName) return dispositionFileName;

    const cleanPath = parsedUrl.pathname || "";
    const parts = cleanPath.split("/");
    return decodeURIComponent(parts[parts.length - 1] || "");
  } catch (err) {
    try {
      const cleanUrl = fileUrl.split("?")[0].split("#")[0];
      const parts = cleanUrl.split("/");
      return decodeURIComponent(parts[parts.length - 1] || "");
    } catch (fallbackErr) {
      return "";
    }
  }
};

const getFileExtension = (fileNameOrUrl) => {
  const rawValue = fileNameOrUrl ? String(fileNameOrUrl) : "";
  const value =
    rawValue.indexOf("?") !== -1 || rawValue.indexOf("/") !== -1
      ? getFileNameFromUrl(rawValue) || rawValue
      : rawValue;
  const cleanValue = value.split("?")[0].split("#")[0];
  const parts = cleanValue.split(".");
  if (parts.length < 2) return "";
  return parts[parts.length - 1].toLowerCase();
};

const formatFileSize = (size) => {
  const bytes = Number(size);
  if (!bytes || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const AUDIO_BROWSER_EXTENSIONS = ["mp3", "mpeg", "wav", "m4a", "aac", "ogg", "oga", "opus", "webm"];
const VIDEO_BROWSER_EXTENSIONS = ["mp4", "m4v", "webm", "ogv", "ogg", "mov"];

const getBestPlayableUrlCandidate = (rawAttachment) => {
  if (!rawAttachment || typeof rawAttachment !== "object") return "";
  return (
    rawAttachment.browserPlayableUrl ||
    rawAttachment.playableUrl ||
    rawAttachment.transcodedUrl ||
    rawAttachment.convertedUrl ||
    rawAttachment.previewUrl ||
    rawAttachment.audioPreviewUrl ||
    rawAttachment.videoPreviewUrl ||
    rawAttachment.streamUrl ||
    rawAttachment.streamingUrl ||
    rawAttachment.mp3Url ||
    rawAttachment.m4aUrl ||
    rawAttachment.mp4Url ||
    ""
  );
};

const isBrowserPlayableAudio = (attachment) => {
  if (!attachment) return false;
  const mimeType = attachment.mimeType ? String(attachment.mimeType).toLowerCase() : "";
  const extension =
    getFileExtension(attachment.fileName) ||
    getFileExtension(attachment.filename) ||
    getFileExtension(attachment.name) ||
    getFileExtension(attachment.originalName) ||
    getFileExtension(attachment.originalFilename) ||
    getFileExtension(attachment.url) ||
    getFileExtension(attachment.fileUrl) ||
    getFileExtension(attachment.mediaUrl) ||
    getFileExtension(attachment.attachmentUrl) ||
    getFileExtension(attachment.signedUrl) ||
    getFileExtension(attachment.playableUrl) ||
    getFileExtension(attachment.browserPlayableUrl);

  if (extension === "amr" || extension === "awb" || mimeType.indexOf("amr") !== -1) {
    return false;
  }

  return (
    AUDIO_BROWSER_EXTENSIONS.indexOf(extension) !== -1 ||
    mimeType.indexOf("audio/mpeg") === 0 ||
    mimeType.indexOf("audio/mp3") === 0 ||
    mimeType.indexOf("audio/wav") === 0 ||
    mimeType.indexOf("audio/x-wav") === 0 ||
    mimeType.indexOf("audio/mp4") === 0 ||
    mimeType.indexOf("audio/aac") === 0 ||
    mimeType.indexOf("audio/ogg") === 0 ||
    mimeType.indexOf("audio/webm") === 0
  );
};

const isBrowserPlayableVideo = (attachment) => {
  if (!attachment) return false;
  const mimeType = attachment.mimeType ? String(attachment.mimeType).toLowerCase() : "";
  const extension =
    getFileExtension(attachment.fileName) ||
    getFileExtension(attachment.filename) ||
    getFileExtension(attachment.name) ||
    getFileExtension(attachment.originalName) ||
    getFileExtension(attachment.originalFilename) ||
    getFileExtension(attachment.url) ||
    getFileExtension(attachment.fileUrl) ||
    getFileExtension(attachment.mediaUrl) ||
    getFileExtension(attachment.attachmentUrl) ||
    getFileExtension(attachment.signedUrl) ||
    getFileExtension(attachment.playableUrl) ||
    getFileExtension(attachment.browserPlayableUrl);

  return (
    VIDEO_BROWSER_EXTENSIONS.indexOf(extension) !== -1 ||
    mimeType.indexOf("video/mp4") === 0 ||
    mimeType.indexOf("video/webm") === 0 ||
    mimeType.indexOf("video/ogg") === 0 ||
    mimeType.indexOf("video/quicktime") === 0
  );
};

const inferAttachmentKind = (attachment) => {
  if (!attachment) return "file";
  const mimeType = attachment.mimeType ? String(attachment.mimeType).toLowerCase() : "";
  const extension =
    getFileExtension(attachment.fileName) ||
    getFileExtension(attachment.filename) ||
    getFileExtension(attachment.name) ||
    getFileExtension(attachment.originalName) ||
    getFileExtension(attachment.originalFilename) ||
    getFileExtension(attachment.url) ||
    getFileExtension(attachment.fileUrl) ||
    getFileExtension(attachment.mediaUrl) ||
    getFileExtension(attachment.attachmentUrl) ||
    getFileExtension(attachment.signedUrl) ||
    getFileExtension(attachment.playableUrl) ||
    getFileExtension(attachment.browserPlayableUrl);

  if (mimeType.indexOf("image/") === 0 || ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "bmp"].indexOf(extension) !== -1) {
    return "image";
  }

  if (mimeType.indexOf("pdf") !== -1 || extension === "pdf") {
    return "pdf";
  }

  if (mimeType.indexOf("audio/") === 0 || ["mp3", "mpeg", "wav", "m4a", "aac", "ogg", "oga", "opus", "amr", "awb", "flac", "webm"].indexOf(extension) !== -1) {
    return "audio";
  }

  if (mimeType.indexOf("video/") === 0 || ["mp4", "mov", "m4v", "webm", "avi", "wmv", "mkv", "3gp", "3g2", "ogv"].indexOf(extension) !== -1) {
    return "video";
  }

  return "file";
};

const normalizeAttachment = (rawAttachment) => {
  if (!rawAttachment) return null;

  if (typeof rawAttachment === "string") {
    const urlValue = rawAttachment;
    const fileNameValue = getFileNameFromUrl(urlValue);
    const normalizedStringAttachment = {
      url: urlValue,
      fileName: fileNameValue,
      mimeType: "",
      fileSize: null,
      playableUrl: "",
    };
    const kind = inferAttachmentKind(normalizedStringAttachment);
    return {
      ...normalizedStringAttachment,
      kind,
      playableUrl:
        kind === "audio" && isBrowserPlayableAudio(normalizedStringAttachment)
          ? urlValue
          : kind === "video" && isBrowserPlayableVideo(normalizedStringAttachment)
            ? urlValue
            : "",
      needsConversion:
        (kind === "audio" && !isBrowserPlayableAudio(normalizedStringAttachment)) ||
        (kind === "video" && !isBrowserPlayableVideo(normalizedStringAttachment)),
    };
  }

  if (typeof rawAttachment !== "object") return null;

  const urlValue =
    rawAttachment.url ||
    rawAttachment.fileUrl ||
    rawAttachment.mediaUrl ||
    rawAttachment.attachmentUrl ||
    rawAttachment.signedUrl ||
    rawAttachment.secureUrl ||
    rawAttachment.location ||
    rawAttachment.href ||
    "";

  const playableUrlValue = getBestPlayableUrlCandidate(rawAttachment);

  const normalizedObjectAttachment = {
    url: urlValue,
    fileName:
      rawAttachment.fileName ||
      rawAttachment.filename ||
      rawAttachment.name ||
      rawAttachment.originalName ||
      rawAttachment.originalFilename ||
      rawAttachment.key ||
      getFileNameFromUrl(urlValue) ||
      getFileNameFromUrl(playableUrlValue) ||
      "Attachment",
    mimeType:
      rawAttachment.mimeType ||
      rawAttachment.contentType ||
      rawAttachment.mediaContentType ||
      rawAttachment.type ||
      "",
    fileSize:
      rawAttachment.fileSize ||
      rawAttachment.size ||
      rawAttachment.bytes ||
      rawAttachment.contentLength ||
      null,
    playableUrl: playableUrlValue,
  };

  const kind = inferAttachmentKind(normalizedObjectAttachment);
  const playableCandidate = normalizedObjectAttachment.playableUrl;

  return {
    ...normalizedObjectAttachment,
    kind,
    playableUrl:
      playableCandidate ||
      (kind === "audio" && isBrowserPlayableAudio(normalizedObjectAttachment)
        ? urlValue
        : kind === "video" && isBrowserPlayableVideo(normalizedObjectAttachment)
          ? urlValue
          : ""),
    needsConversion:
      (kind === "audio" && !playableCandidate && !isBrowserPlayableAudio(normalizedObjectAttachment)) ||
      (kind === "video" && !playableCandidate && !isBrowserPlayableVideo(normalizedObjectAttachment)),
  };
};

const renderOpenLink = (attachment, text) => {
  if (!attachment.url) return null;
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="bwai-chat-attach-link"
    >
      {text} <BsBoxArrowUpRight size={12} />
    </a>
  );
};

const renderAttachment = (rawAttachment) => {
  const attachment = normalizeAttachment(rawAttachment);
  if (!attachment) return null;

  const sizeText = formatFileSize(attachment.fileSize);

  if (attachment.kind === "image" && attachment.url) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className={
          attachment.fileName
            ? "bwai-chat-attach-image-link"
            : "bwai-chat-attach-image-link bwai-chat-attach-image-link--no-name"
        }
      >
        <img
          src={attachment.url}
          alt={attachment.fileName || "Attachment"}
          className="bwai-chat-attach-image"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      </a>
    );
  }

  if (attachment.kind === "audio") {
    return (
      <div className="bwai-chat-attach-card">
        <div className="bwai-chat-attach-name">
          {attachment.fileName || "Audio attachment"}
        </div>
        {sizeText ? <div className="bwai-chat-attach-meta">{sizeText}</div> : null}
        {attachment.playableUrl ? (
          <audio
            controls
            preload="metadata"
            src={attachment.playableUrl}
            className="bwai-chat-attach-audio"
          >
            Your browser does not support the audio element.
          </audio>
        ) : (
          <div className="bwai-chat-attach-note">
            Audio preview unavailable for this format. Open or download the file.
          </div>
        )}
        {renderOpenLink(attachment, attachment.playableUrl ? "Open audio" : "Open/download audio")}
      </div>
    );
  }

  if (attachment.kind === "video") {
    return (
      <div className="bwai-chat-attach-card">
        {attachment.playableUrl ? (
          <video
            controls
            preload="metadata"
            src={attachment.playableUrl}
            className="bwai-chat-attach-video"
          >
            Your browser does not support the video element.
          </video>
        ) : null}
        <div
          className={
            attachment.playableUrl
              ? "bwai-chat-attach-info bwai-chat-attach-info--below-media"
              : "bwai-chat-attach-info"
          }
        >
          <span className="bwai-chat-attach-name">
            {attachment.fileName || "Video attachment"}
          </span>
          {sizeText ? <span className="bwai-chat-attach-meta">{sizeText}</span> : null}
          {!attachment.playableUrl ? (
            <span className="bwai-chat-attach-note">
              Video preview unavailable for this format. Open or download the file.
            </span>
          ) : null}
        </div>
        {renderOpenLink(attachment, attachment.playableUrl ? "Open video" : "Open/download video")}
      </div>
    );
  }

  const isPdf = attachment.kind === "pdf";
  const actionText = isPdf ? "View PDF" : "View file";

  return (
    <a
      href={attachment.url || "#"}
      target={attachment.url ? "_blank" : undefined}
      rel={attachment.url ? "noopener noreferrer" : undefined}
      onClick={(event) => {
        if (!attachment.url) event.preventDefault();
      }}
      className="bwai-chat-attach-file"
    >
      {isPdf ? <BsFileEarmarkPdf color="#ff4500" /> : <BsPaperclip />}
      <span className="bwai-chat-attach-file-name">
        {attachment.fileName || (isPdf ? "PDF attachment" : "File attachment")}
      </span>
      {attachment.url ? <BsBoxArrowUpRight size={12} /> : null}
      <span className="sr-only">{actionText}</span>
    </a>
  );
};

function ChatMessage({ item, renderSenderLabel, currentUserId }) {
  const isMine =
    (currentUserId &&
      item.senderId &&
      String(item.senderId) === String(currentUserId)) ||
    item.senderType === "ADMIN";

  const senderLabel = renderSenderLabel(item);
  const timeLabel = item.createdAt
    ? moment.utc(item.createdAt).local().format("h:mm A")
    : "";

  return (
    <div
      className={
        isMine
          ? "chat-message bwai-chat-msg bwai-chat-msg--mine"
          : "chat-message bwai-chat-msg"
      }
    >
      <div className="bwai-chat-meta">
        {senderLabel}{timeLabel ? " · " + timeLabel : ""}
        {item.pending ? " · Sending…" : ""}
        {item.failed ? " · Failed to send" : ""}
      </div>
      <div className="bwai-chat-bubble">
        {item.text ? <div className="bwai-chat-text">{item.text}</div> : null}
        {renderAttachment(item.attachment)}
      </div>
    </div>
  );
}

export default ChatMessage;
